import { Alpha3Code } from 'i18n-iso-countries';
import { DateTime } from 'luxon';
import { FieldValues } from 'react-hook-form';
import { get } from 'lodash';

import identifierTypes, { IdentifierTypes } from '../../constants/identifierTypes';
import Luhn from '../Luhn';

type IdentifierTypeInfo = {
  validator: (value: string) => boolean;
  errorMessage: string;
};

type NationalIdentifierInput = {
  country?: Alpha3Code;
  identifier?: string;
  type?: IdentifierTypes;
};

const swedishDateExtractor = (identifier: string) => {
  const datePart = identifier.substring(0, 8);
  return DateTime.fromFormat(datePart, 'yyyyMMdd', { zone: 'UTC' });
};

const swedishValidator = (identifier: string) => {
  if (!/^\d{12}$/.test(identifier)) return false;

  const date = swedishDateExtractor(identifier);
  if (!date.isValid) return false;

  return Luhn(identifier.slice(2));
};

const coordinationNumberDateExtractor = (identifier: string) => {
  const yearAndMonthPart = identifier.substring(0, 6);
  const dayPart = parseInt(identifier.substring(6, 8), 10) - 60;
  return DateTime.fromFormat(`${yearAndMonthPart}${dayPart < 10 ? `0${dayPart}` : dayPart}`, 'yyyyMMdd', { zone: 'UTC' });
};

const coordinationNumberValidator = (identifier: string) => {
  if (!/^\d{12}$/.test(identifier)) return false;

  const date = coordinationNumberDateExtractor(identifier);
  if (!date.isValid) return false;

  return Luhn(identifier.slice(2));
};

const finnishDateExtractor = (identifier: string) => {
  const daysPart = identifier.substring(0, 2);
  const monthPart = identifier.substring(2, 4);
  const yearPart = identifier.substring(4, 6);
  const centuryDigit = identifier[6];
  let century;
  if (centuryDigit === '-') century = '19';
  else if (centuryDigit === 'A') century = '20';
  else if (centuryDigit === '+') century = '18';
  else throw new Error(`Unexpected century number for Finnish national identifier ${identifier}`);
  return DateTime.fromFormat(`${daysPart}${monthPart}${century}${yearPart}`, 'ddMMyyyy', { zone: 'UTC' });
};

const finnishValidator = (identifier: string) => {
  const regex = /^\d{6}[+-A]\d{3}[0-9A-Y]$/;
  if (!regex.test(identifier)) return false;

  const checksumChars = '0123456789ABCDEFHJKLMNPRSTUVWXY';

  const date = finnishDateExtractor(identifier);
  if (!date.isValid) return false;

  const datePart = identifier.substring(0, 6);
  const individualNumber = identifier.substring(7, 10);
  const expectedChecksum = identifier.charAt(10).toUpperCase();

  const numberForChecksum = parseInt(datePart + individualNumber, 10);
  const calculatedChecksum = checksumChars[numberForChecksum % 31];

  return calculatedChecksum === expectedChecksum;
};

const danishDateExtractor = (identifier: string) => {
  const daysPart = identifier.substring(0, 2);
  const monthPart = identifier.substring(2, 4);
  const yearPart = identifier.substring(4, 6);
  const centuryDigit = identifier[6];
  let century;
  if (parseInt(centuryDigit, 10) <= 3) century = '19';
  else if (centuryDigit === '4' || centuryDigit === '9') {
    if (parseInt(yearPart, 10) <= 36) century = '20';
    else century = '19';
  } else if (parseInt(centuryDigit, 10) <= 8) {
    if (parseInt(yearPart, 10) <= 57) century = '20';
    else century = '18';
  } else {
    throw new Error(`Unexpected century digit ${centuryDigit} in danish CPR number ${identifier}`);
  }
  return DateTime.fromFormat(`${daysPart}${monthPart}${century}${yearPart}`, 'ddMMyyyy', { zone: 'UTC' });
};

const danishValidator = (identifier: string) => {
  if (!/^\d{10}$/.test(identifier)) return false;

  const date = danishDateExtractor(identifier);
  if (!date.isValid) return false;

  return true;
};

const southAfricanDateExtractor = (identifier: string) => {
  const datePart = identifier.substring(0, 6);
  return DateTime.fromFormat(datePart, 'yyMMdd', { zone: 'UTC' });
};

const southAfricanValidator = (identifier: string) => {
  if (!/^\d{11}[0-1][0-9]$/.test(identifier)) return false;

  const date = southAfricanDateExtractor(identifier);
  if (!date.isValid) return false;

  return Luhn(identifier);
};

const ukValidator = (identifier: string) => /^[A-CEGHJ-PR-TW-Z]{2}\d{6}[A-D]$/.test(identifier);

const defaultCountryMap: Partial<Record<IdentifierTypes, IdentifierTypeInfo>> = {
  [identifierTypes.PASSPORT_NUMBER]: {
    validator: (value: string) => /^[A-Z0-9]{1,30}$/.test(value),
    errorMessage: 'Invalid passport number. Only upper case letters and numbers are allowed, max length 30 characters.',
  },
};

const countryIdentifiersMap: Partial<Record<
Alpha3Code,
Partial<Record<IdentifierTypes, IdentifierTypeInfo>>>> = {
  SWE: {
    [identifierTypes.SOCIAL_SECURITY_NUMBER]: {
      validator: swedishValidator,
      errorMessage: 'Invalid social security number. Format: YYYYMMDDXXXX',
    },
    [identifierTypes.COORDINATION_NUMBER]: {
      validator: coordinationNumberValidator,
      errorMessage: 'Invalid coordination number. Format: YYYYMMDDXXXX',
    },
  },
  FIN: {
    [identifierTypes.SOCIAL_SECURITY_NUMBER]: {
      validator: finnishValidator,
      errorMessage: 'Invalid personal identity code. Format: DDMMYYCXXXZ',
    },
  },
  DNK: {
    [identifierTypes.SOCIAL_SECURITY_NUMBER]: {
      validator: danishValidator,
      errorMessage: 'Invalid CPR number. Format: DDMMYYXXXX',
    },
  },
  ZAF: {
    [identifierTypes.SOCIAL_SECURITY_NUMBER]: {
      validator: southAfricanValidator,
      errorMessage: 'Invalid ID number. Format: YYMMDDXXXXXXC',
    },
  },
  GBR: {
    [identifierTypes.SOCIAL_SECURITY_NUMBER]: {
      validator: ukValidator,
      errorMessage: 'Invalid National Insurance Number (NINo). Format: XX999999X',
    },
  },
};

export const validateNationalIdentifierUniqueCountryAndType = (
  type: string,
  allValues: FieldValues,
  country?: string,
) => {
  if (!country || !type) {
    return undefined;
  }

  const nationalIdentifiers = (allValues?.nationalIdentifier ?? []) as Array<{
    country?: string;
    type?: string;
  }>;

  const sameCountryAndTypeCount = nationalIdentifiers
    .filter((item) => item.country === country && item.type === type)
    .length;

  if (sameCountryAndTypeCount > 1) {
    return 'Country and type combination must be unique';
  }

  return undefined;
};

const validateNationalIdentifier = ({
  country,
  identifier,
  type,
}: NationalIdentifierInput) => {
  if (!country || !type || !identifier) return undefined;

  const typeValidators = get(countryIdentifiersMap, country, defaultCountryMap);
  const validatorInfo = typeValidators[type];

  if (!validatorInfo) {
    const supportedTypes = Object.keys(typeValidators)
      .map((identifierType) => `"${identifierType}"`)
      .join(', ');

    return `"type" ${type} is not valid for country "${country}". Supported types: ${supportedTypes}`;
  }

  const isValid = validatorInfo.validator(identifier);
  if (!isValid) {
    return `"identifier" is not in a valid format for country "${country}" and type "${type}": ${validatorInfo.errorMessage}`;
  }

  return undefined;
};

export default validateNationalIdentifier;
